import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  ArrowLeft, 
  History, 
  RotateCcw,
  Clock,
  User,
  FileText
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractVersions({ user, setUser }) {
  const navigate = useNavigate();
  const { contractId } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoreDialog, setRestoreDialog] = useState({ open: false, version: null });

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  const fetchContract = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setContract(data);
      } else {
        toast.error("Contrat non trouvé");
        navigate('/contracts');
      }
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreDialog.version) return;

    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/restore/${restoreDialog.version}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success(`Version ${restoreDialog.version} restaurée`);
        fetchContract();
      } else {
        toast.error("Erreur lors de la restauration");
      }
    } catch (error) {
      toast.error("Erreur lors de la restauration");
    }

    setRestoreDialog({ open: false, version: null });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AppLayout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-screen">
          <div className="spinner w-8 h-8"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8 max-w-4xl mx-auto" data-testid="contract-versions-page">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-semibold tracking-tight">
              Historique des versions
            </h1>
            <p className="text-sm text-muted-foreground">
              {contract?.name}
            </p>
          </div>
        </div>

        {/* Current Version */}
        <Card className="mb-8" data-testid="current-version-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Version actuelle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Badge className="bg-green-100 text-green-800 text-lg px-3 py-1">
                  v{contract?.version}
                </Badge>
                <div className="text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatDate(contract?.updated_at)}
                  </div>
                </div>
              </div>
              <Button onClick={() => navigate(`/contracts/${contractId}`)}>
                Modifier
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Version Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5" />
              Historique complet
            </CardTitle>
            <CardDescription>
              Toutes les versions de ce contrat
            </CardDescription>
          </CardHeader>
          <CardContent>
            {contract?.versions?.length > 0 ? (
              <div className="version-timeline">
                {[...contract.versions].reverse().map((version, index) => (
                  <div 
                    key={version.version} 
                    className="version-timeline-item"
                    data-testid={`version-${version.version}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={version.version === contract.version ? "default" : "outline"}
                          >
                            Version {version.version}
                          </Badge>
                          {version.version === contract.version && (
                            <Badge className="bg-green-100 text-green-800">
                              Actuelle
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(version.created_at)}
                          </div>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {version.created_by}
                          </div>
                        </div>
                        
                        {/* Show changed fields summary */}
                        <div className="mt-2 text-xs">
                          <span className="text-muted-foreground">
                            {Object.keys(version.data || {}).length} champs renseignés
                          </span>
                        </div>
                      </div>
                      
                      {version.version !== contract.version && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRestoreDialog({ open: true, version: version.version })}
                          data-testid={`restore-version-${version.version}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restaurer
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Aucun historique disponible
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Restore Confirmation Dialog */}
        <AlertDialog open={restoreDialog.open} onOpenChange={(open) => setRestoreDialog({ ...restoreDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Restaurer cette version ?</AlertDialogTitle>
              <AlertDialogDescription>
                Le contrat sera restauré à la version {restoreDialog.version}. 
                Une nouvelle version sera créée avec les données de la version sélectionnée.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleRestore}>
                Restaurer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
